import React from "react";
import { Alert, Input, Modal } from "antd";
import useLang from "../../../hooks/useLang";
import { PRIMARY_BUTTON_STYLE } from "./styles";

/** Typed into the confirmation box before a money-moving operation can run. */
export const CONFIRMATION_WORD = "KONFIRMO";

interface ConfirmModalProps {
	open: boolean;
	operationLabel: string;
	orderCount: number;
	/** Omitted by categories that have no driver — the row is then not rendered. */
	driverLabel?: string;
	/** One extra "label: value" line, e.g. the target country for a section change. */
	detail?: { label: string; value: string };
	/** Money-moving variant: red button + typed confirmation. */
	danger: boolean;
	dangerWarning?: string;
	confirmWord: string;
	onConfirmWordChange: (value: string) => void;
	submitting: boolean;
	onOk: () => void;
	onCancel: () => void;
}

const ConfirmModal = ({
	open,
	operationLabel,
	orderCount,
	driverLabel,
	detail,
	danger,
	dangerWarning,
	confirmWord,
	onConfirmWordChange,
	submitting,
	onOk,
	onCancel,
}: ConfirmModalProps) => {
	const { strings } = useLang("bulkOrderOperations");
	const common = strings?.common;

	const confirmWordOk =
		!danger || confirmWord.trim().toUpperCase() === CONFIRMATION_WORD;

	return (
		<Modal
			open={open}
			title={common?.confirmTitle ?? "Confirm operation"}
			okText={common?.confirm ?? "Confirm"}
			cancelText={common?.cancel ?? "Cancel"}
			okButtonProps={{
				danger,
				style: danger ? undefined : PRIMARY_BUTTON_STYLE,
				loading: submitting,
				disabled: !confirmWordOk,
			}}
			onOk={onOk}
			onCancel={onCancel}
		>
			<div className="flex flex-col gap-2 py-2">
				<div>
					<span className="font-medium">
						{common?.confirmOperation ?? "Operation"}:{" "}
					</span>
					{operationLabel}
				</div>
				<div>
					<span className="font-medium">
						{common?.confirmOrderCount ?? "Number of orders"}:{" "}
					</span>
					{orderCount}
				</div>
				{driverLabel && (
					<div>
						<span className="font-medium">
							{common?.confirmDriver ?? "Driver"}:{" "}
						</span>
						{driverLabel}
					</div>
				)}
				{detail && (
					<div>
						<span className="font-medium">{detail.label}: </span>
						{detail.value}
					</div>
				)}
				{danger && (
					<>
						<Alert type="error" showIcon message={dangerWarning} />
						<div className="font-medium">
							{common?.typeToConfirm ??
								`Type ${CONFIRMATION_WORD} to enable the button`}
						</div>
						<Input
							autoFocus
							value={confirmWord}
							onChange={(e) =>
								onConfirmWordChange(e.target.value)
							}
						/>
					</>
				)}
			</div>
		</Modal>
	);
};

export default ConfirmModal;
